import React, { useLayoutEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ContentSafetyButton } from '../../AccountSafety';
import './CalendarReportMenu.css';

const TEXT = {
  more: { ko: '더 보기', en: 'More options', fr: 'Plus d’options' },
  report: { ko: '일정 신고', en: 'Report an event', fr: 'Signaler un événement' },
};
const VIEWPORT_INSET = 12;
const TRIGGER_GAP = 8;

export default function CalendarReportMenu({ events, lang, onOpen }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const panelID = useId();
  const t = key => TEXT[key][lang] || TEXT[key].fr;

  useLayoutEffect(() => {
    if (!open) return undefined;

    const reposition = () => {
      const trigger = triggerRef.current.getBoundingClientRect();
      const width = panelRef.current.offsetWidth;
      const top = trigger.bottom + TRIGGER_GAP;
      setPosition({
        top,
        left: Math.max(VIEWPORT_INSET, Math.min(trigger.right - width, window.innerWidth - width - VIEWPORT_INSET)),
        maxHeight: Math.max(0, window.innerHeight - top - VIEWPORT_INSET),
      });
    };
    const closeOutside = event => {
      if (!triggerRef.current.contains(event.target) && !panelRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const closeOnEscape = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        triggerRef.current.focus();
      }
    };
    reposition();
    panelRef.current.querySelector('summary').focus();
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('focusin', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('focusin', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  return <>
    <button
      type="button"
      ref={triggerRef}
      className="help-icon-btn calendar-more-button"
      title={t('more')}
      aria-label={t('more')}
      aria-expanded={open}
      aria-controls={open ? panelID : undefined}
      onClick={() => {
        if (!open) onOpen?.();
        setOpen(value => !value);
      }}
    >⋯</button>
    {open && createPortal(
      <div ref={panelRef} id={panelID} className="calendar-report-popover" style={position}>
        <details>
          <summary>{t('report')}</summary>
          <div className="safety-content" onClickCapture={event => {
            if (event.target.closest('button')) setOpen(false);
          }}>
            {events.map(event => <div className="safety-row" key={event.id}>
              <span>{event.title} · {new Date(event.start_time).toLocaleDateString(lang)}</span>
              <ContentSafetyButton kind="event" contentID={event.id} authorID={event.created_by} />
            </div>)}
          </div>
        </details>
      </div>,
      document.body
    )}
  </>;
}
